import { useState } from 'react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { Check, Lock } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import useBoardStore from '../../../store/boardStore';

export default function DraggableBoardItem({
  board,
  active,
  onClick,
  children,
  sourceFolderId = null,
  sourceWsFolderId = null,
  indent = 9,
  selectable = false,
  selected = false,
  anySelected = false,
  onToggleSelect,
  // Render-prop opcional: ({ anchorPoint, onClose }) => <BoardItemMenu controlado />
  // Quando fornecida, o clique-direito abre o menu na posição do cursor.
  renderContextMenu,
}) {
  const [ctxPoint, setCtxPoint] = useState(null);

  const handleContextMenu = (e) => {
    if (!renderContextMenu) return;
    // Em modo de seleção múltipla, não abre menu de item individual
    if (selectable && anySelected) return;
    e.preventDefault();
    e.stopPropagation();
    setCtxPoint({ x: e.clientX, y: e.clientY });
  };
  const suffix = sourceFolderId
    ? `-fav-${sourceFolderId}`
    : sourceWsFolderId
      ? `-wsf-${sourceWsFolderId}`
      : '';
  const dragId = `board-${board.id}${suffix}`;
  const { attributes, listeners, setNodeRef: setDragRef, transform, isDragging } = useDraggable({
    id: dragId,
    data: { type: 'board', boardId: board.id, sourceFolderId, sourceWsFolderId },
  });
  // Único droppable cobrindo o item inteiro; metade calculada em AppLayout com hysteresis.
  const { setNodeRef: setDropRef } = useDroppable({
    id: `drop-${dragId}`,
    data: {
      type: 'board-slot',
      boardId: board.id,
      folderId: sourceWsFolderId,
      favoriteFolderId: sourceFolderId,
    },
  });

  // Lê hint global (calculado em AppLayout.handleDragOver com hysteresis)
  const dropHint = useBoardStore(
    useShallow((s) => (s.sidebarDropHint?.boardId === board.id ? s.sidebarDropHint : null))
  );

  const setItemRef = (node) => {
    setDropRef(node);
  };

  const style = isDragging ? {} : { transform: CSS.Translate.toString(transform) };

  const handleClick = (e) => {
    if (selectable && anySelected) {
      e.preventDefault();
      e.stopPropagation();
      onToggleSelect?.(board.id);
      return;
    }
    onClick?.(e);
  };

  // Indicador de inserção (gap + linha azul fina + placeholder tracejado)
  const showBefore = dropHint?.position === 'before';
  const showAfter = dropHint?.position === 'after';

  return (
    <div className="relative">
      {showBefore && (
        <div className="mx-2 mb-0.5" aria-hidden="true">
          <div className="h-1 bg-accent rounded-full" />
          <div className="h-7 mt-0.5 rounded-md border border-dashed border-accent/50 bg-accent/5" />
        </div>
      )}

      <div style={isDragging ? { height: 0, overflow: 'hidden' } : undefined}>
        <div ref={setItemRef} className="relative">
          <div
            ref={setDragRef}
            style={style}
            {...attributes}
            {...listeners}
            onClick={handleClick}
            onContextMenu={handleContextMenu}
            className={`group flex items-center pr-2 py-1.5 text-sm cursor-pointer transition-colors select-none rounded-md ${
              selected
                ? 'bg-accent/15 text-text-primary'
                : active
                  ? 'text-text-primary bg-white/10'
                  : 'text-text-secondary hover:text-text-primary hover:bg-white/5'
            }`}
          >
            {selectable && (
              <button
                onClick={(e) => { e.stopPropagation(); onToggleSelect?.(board.id); }}
                onPointerDown={(e) => e.stopPropagation()}
                className={`ml-1 w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-all ${
                  selected
                    ? 'bg-accent border-accent opacity-100'
                    : `border-text-secondary/50 ${anySelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`
                }`}
                title={selected ? 'Desmarcar' : 'Selecionar'}
              >
                {selected && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
              </button>
            )}
            <span
              className="truncate flex-1"
              style={{ paddingLeft: `${(selectable ? indent - 1 : indent) * 4}px` }}
            >
              {board.name}
            </span>
            {/* Quadro que pede senha para abrir. Aqui, e nao nos tres lugares
                que montam este item (AppLayout, WorkspaceFolder,
                FavoriteFolder): o indicador fica igual nas tres listas de
                graca. Ausente no board (backend antigo) = sem cadeado. */}
            {board.is_protected && (
              <Lock
                className="w-3 h-3 ml-1 shrink-0 text-text-secondary/70"
                title="Pede senha para abrir"
              />
            )}
            {children}
          </div>
        </div>
      </div>

      {showAfter && (
        <div className="mx-2 mt-0.5" aria-hidden="true">
          <div className="h-1 bg-accent rounded-full" />
          <div className="h-7 mt-0.5 rounded-md border border-dashed border-accent/50 bg-accent/5" />
        </div>
      )}

      {ctxPoint && renderContextMenu?.({ anchorPoint: ctxPoint, onClose: () => setCtxPoint(null) })}
    </div>
  );
}
